import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { resolveInternalLinks } from "@/content/internal-links";

type Props = {
  eyebrow?: string;
  title?: React.ReactNode;
  description?: string;
  hrefs: string[];
};

/**
 * Reusable "Related reading" / "Deep-dive guides" block used on service,
 * location, and industry pages. Anchors are resolved through the canonical
 * LINK_LABELS registry so anchor text stays consistent site-wide.
 */
export function RelatedReading({ eyebrow = "Deep-dive guides", title, description, hrefs }: Props) {
  const items = resolveInternalLinks(hrefs);
  if (items.length === 0) return null;
  return (
    <Section eyebrow={eyebrow} title={title ?? <>Keep learning — <span className="text-gradient">related guides.</span></>}>
      {description && (
        <p className="max-w-2xl text-muted-foreground -mt-8 mb-10">{description}</p>
      )}
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <li key={it.href}>
            <Link
              to={it.href as never}
              className="group flex items-start gap-3 rounded-2xl border border-border p-5 hover:bg-card hover:border-primary/40 transition"
            >
              <ArrowUpRight size={16} className="mt-1 text-primary shrink-0" />
              <span className="text-sm font-medium leading-snug group-hover:text-primary transition">{it.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}
